import { Button } from '@design/components/button/Button';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { Watchlist } from '@terminal/stores/use-watchlist/watchlist-types';
import { useWatchlistManagerWidget } from '@terminal/stores/use-watchlist-manager-widget/useWatchlistManagerWidget';
import { widgetPaths } from '@terminal/views/widgets/widget-utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './WatchlistCard.module.scss';

export interface WatchlistCardProps {
  watchlist: Watchlist;
  checked: boolean;
  onToggle: () => void;
  trackingSource?: PosthogTrackingSource;
}

export const WatchlistCard = ({
  watchlist,
  checked,
  onToggle,
  trackingSource,
}: WatchlistCardProps) => {
  const { t } = useTranslation(['stream']);
  const navigate = useNavigate();
  const { setQueryParams } = useQueryParams();
  const { trackWatchlistOpen } = usePosthogEvents();
  const setIsEditMode = useWatchlistManagerWidget(state => state.setIsEditMode);

  const handleExpand = () => {
    if (trackingSource) {
      trackWatchlistOpen(trackingSource, watchlist.id);
    }

    navigate(`${appPaths.watchlist}/${watchlist.id}`);
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setQueryParams({
      [queryKeys.widget]: widgetPaths.editWatchlistModal,
      [queryKeys.watchlistId]: watchlist.id,
    });
  };

  const menuItems: DropdownEntry[] = [
    {
      label: t('stream:manageFollowing.watchlistCard.menu.edit'),
      key: 'edit',
      iconName: 'Pencil',
      onClick: handleEdit,
    },
    {
      label: t('stream:manageFollowing.watchlistCard.menu.expand'),
      key: 'expand',
      iconName: 'Expand',
      onClick: handleExpand,
    },
  ];

  return (
    <div className={styles.card} data-testid={`watchlist-card-${watchlist.id}`}>
      <div className={styles.wrapper}>
        <CheckBox
          className={styles.watchlistCheckbox}
          name={`watchlist-card-${watchlist.id}`}
          id={`watchlist-card-checkbox-${watchlist.id}`}
          label={<p className={styles.title}>{watchlist.name}</p>}
          checked={checked}
          onChange={() => onToggle()}
        />
      </div>
      <div>
        <DropdownMenu
          align="end"
          className={styles.watchlistActions}
          triggerNode={
            <Button
              className={styles.menuButton}
              iconName="EllipsisVertical"
              variant="empty"
              size="xsmall"
              data-testid={`watchlist-card-menu-${watchlist.id}`}
              aria-label={t('stream:manageFollowing.watchlistCard.menu.label')}
            />
          }
        >
          <DropdownGroup items={menuItems} className={styles.menuItem} />
        </DropdownMenu>
      </div>
    </div>
  );
};
